/** Dashboard header “view as” role; filters Overview (and later other tabs). */
export type DashboardViewRole =
  | 'all'
  | 'manager'
  | 'provider'
  | 'hygienist'
  | 'front_desk'
  | 'owner'
  | 'assistant'

export const DASHBOARD_VIEW_ROLE_OPTIONS: {
  value: DashboardViewRole
  label: string
}[] = [
  { value: 'all', label: 'All' },
  { value: 'manager', label: 'Manager' },
  { value: 'provider', label: 'Provider' },
  { value: 'hygienist', label: 'Hygienist' },
  { value: 'front_desk', label: 'Front Desk/Staff' },
  { value: 'owner', label: 'Owner' },
  { value: 'assistant', label: 'Assistant' },
]

/**
 * Maps a dashboard view role to the persona key used by dashboard settings
 * (`DASHBOARD_PERSONAS` / the settings map). The settings persona ids use
 * kebab-case (`front-desk`) whereas view roles use snake_case (`front_desk`).
 * `all` has no persona (it shows everything), so it is intentionally omitted.
 */
export const DASHBOARD_VIEW_ROLE_TO_PERSONA: Partial<
  Record<DashboardViewRole, string>
> = {
  owner: 'owner',
  manager: 'manager',
  provider: 'provider',
  hygienist: 'hygienist',
  assistant: 'assistant',
  front_desk: 'front-desk',
}

/**
 * Maps a signed-in user's `work_role` (preferred) or `user_role` (fallback)
 * to the dashboard view role used to seed the persona. Backend strings vary in
 * spelling (e.g. "Office Manager", "Dental Hygienist"). Unknown → `manager`.
 */
const USER_ROLE_TO_VIEW_ROLE: Record<string, DashboardViewRole> = {
  owner: 'owner',
  practice_owner: 'owner',
  manager: 'manager',
  office_manager: 'manager',
  practice_manager: 'manager',
  provider: 'provider',
  dentist: 'provider',
  associate_dentist: 'provider',
  pediatric_dentist: 'provider',
  dental_therapist: 'provider',
  anesthesiologist: 'provider',
  oral_surgeon: 'provider',
  oral_and_maxillofacial_surgeon: 'provider',
  oral_pathologist: 'provider',
  periodontist: 'provider',
  endodontist: 'provider',
  orthodontist: 'provider',
  prosthodontist: 'provider',
  hygienist: 'hygienist',
  dental_hygienist: 'hygienist',
  assistant: 'assistant',
  dental_assistant: 'assistant',
  dental_asst: 'assistant',
  orthodontic_assistant: 'assistant',
  dental_nurse: 'assistant',
  dental_technician: 'assistant',
  front_desk: 'front_desk',
  front_desk_staff: 'front_desk',
  receptionist: 'front_desk',
  billing_and_insurance_coordinator: 'front_desk',
}

/**
 * Resolves the dashboard view role from a raw backend role string
 * (`work_role` preferred, `user_role` as fallback). Normalizes
 * spacing/casing/hyphens before lookup; defaults to `manager`.
 */
export function resolveViewRoleFromUserRole(
  role: string | undefined | null,
): DashboardViewRole {
  if (!role) return 'manager'
  const key = role
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
  return USER_ROLE_TO_VIEW_ROLE[key] ?? 'manager'
}

export function resolveSignedInDashboardViewRole(
  workRole?: string | null,
  userRoleFallback?: string | null,
): DashboardViewRole {
  return resolveViewRoleFromUserRole(workRole ?? userRoleFallback)
}
